/*
 * The common config is a js file to make it possible to add documentation
 * to the configuration. It's highly recommended to add all possible config
 * variables here so it's also clear to anyone reading the code what could
 * affect the code. Other config files may be JSON files to reduce typing.
 */
const appName = require('../package.json').name;

const actorTypes = {
  MasterAdmin: 'masterAdmin',
  OrganizationAdmin: 'organizationAdmin',
  OrganizationOwner: 'organizationOwner',
  RegionalGeneralManager: 'regionalGeneralManager',
  DistrictManager: 'districtManager',
  LocationManager: 'locationManager',
  AssistantGeneralManager: 'assistantGeneralManager',
  ServiceAdvisor: 'serviceAdvisor',
  Technician: 'technician',
  OrganizationCustomerSupport: 'organizationCustomerSupport',
  RegionalCustomerSupport: 'regionalCustomerSupport',
  Staff: 'staff',
  Member: 'member',
  ServiceCenter: 'serviceCenter',
  Guest: 'guest',
  Marketing: 'marketing',
};

const actorRanks = {
  [actorTypes.MasterAdmin]: 1,
  [actorTypes.OrganizationOwner]: 2,
  [actorTypes.OrganizationAdmin]: 3,
  [actorTypes.RegionalGeneralManager]: 4,
  [actorTypes.DistrictManager]: 5,
  [actorTypes.LocationManager]: 6,
  [actorTypes.AssistantGeneralManager]: 7,
  [actorTypes.ServiceAdvisor]: 8,
  [actorTypes.Technician]: 9,
  [actorTypes.OrganizationCustomerSupport]: 10,
  [actorTypes.RegionalCustomerSupport]: 11,
  [actorTypes.Staff]: 12,
  [actorTypes.ServiceCenter]: 13,
  [actorTypes.Marketing]: 14,
  [actorTypes.Member]: 15,
  [actorTypes.Guest]: 14,
};

// Maximum rank allowed to access admin-new app (DistrictManager and above)
const ADMIN_APP_MAX_RANK = 5;

const viewModes = {
  Master: 'master',
  Organization: 'organization',
  Region: 'region',
  District: 'district',
  Location: 'location',
};

module.exports = {
  // The name of this app for any self-referential monitoring logic
  appName,
  // The Next server port
  port: 80,
  // The Sentry DSN (replace with a new DSN for your service)
  sentryDsn: 'https://c547a58b4e6d4a73bb65a1049da1c42d@sentry.io/1726937',
  googleMapsApiKey: 'AIzaSyBRoWdCUAnj7HtGWjsyhUYFg7diuhZbtdQ',
  // Toggle sending to sentry
  useSentry: false,
  // Sensitive information that should be omitted from logs/tickets
  sensitiveFields: ['password', 'currentPassword', 'newPassword', 'passwordHash'],
  authHost: 'https://auth.beta.stressfree.com/v1',
  //authHost: 'http://localhost:8084/v1',
  graphql: {
    url: 'https://graphql.beta.stressfree.com',
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'pass-mobile-version': '3.0.0',
    },
    compress: false,
  },
  routes: {
    login: {
      path: '/login',
    },
    forgotPassword: {
      path: '/forgot-password',
    },
    resetPassword: {
      path: '/reset-password',
    },
    coupons: {
      path: '/coupons',
      pageName: 'Coupons',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
      ],
      viewModes: [viewModes.Region, viewModes.District, viewModes.Location],
      navBarItemInViewModes: [viewModes.Region, viewModes.District, viewModes.Location],
    },
    appointments: {
      path: '/appointments-old',
      pageName: 'Appointments Old',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.OrganizationCustomerSupport,
        actorTypes.RegionalCustomerSupport,
      ],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
      navBarItemInViewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
    },
    appointmentsNew: {
      path: '/appointments',
      pageName: 'Appointments',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.OrganizationCustomerSupport,
        actorTypes.RegionalCustomerSupport,
      ],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
      navBarItemInViewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
    },
    leaderboards: {
      path: '/leaderboards',
      pageName: 'Leaderboards',
      fullWidth: true,
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.OrganizationCustomerSupport,
        actorTypes.RegionalCustomerSupport,
      ],
      viewModes: [viewModes.Region, viewModes.District, viewModes.Location, viewModes.Organization],
      navBarItemInViewModes: [viewModes.Region, viewModes.District, viewModes.Location, viewModes.Organization],
    },
    scorecards: {
      path: '/scorecards',
      pageName: 'Scorecards',
      fullWidth: true,
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.OrganizationCustomerSupport,
        actorTypes.RegionalCustomerSupport,
      ],
      viewModes: [viewModes.Region, viewModes.District, viewModes.Location, viewModes.Organization],
      navBarItemInViewModes: [viewModes.Region, viewModes.District, viewModes.Location, viewModes.Organization],
    },
    reports: {
      path: '/reports',
      pageName: 'Reports',
      fullWidth: true,
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.OrganizationCustomerSupport,
      ],
      viewModes: [viewModes.Region, viewModes.District, viewModes.Location, viewModes.Organization],
      navBarItemInViewModes: [viewModes.Region, viewModes.District, viewModes.Location, viewModes.Organization],
    },
    home: {
      path: '/',
      pageName: 'Home',
      fullWidth: true,
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.Technician,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.OrganizationCustomerSupport,
        actorTypes.RegionalCustomerSupport,
        actorTypes.Marketing,
      ],
      viewModes: [viewModes.Region, viewModes.District, viewModes.Location, viewModes.Organization],
      navBarItemInViewModes: [viewModes.Region, viewModes.District, viewModes.Location, viewModes.Organization],
    },
    team: {
      path: '/team',
      pageName: 'Team',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.OrganizationCustomerSupport,
        actorTypes.RegionalCustomerSupport,
      ],
      viewModes: [viewModes.Location, viewModes.Region, viewModes.District, viewModes.Organization, viewModes.Master],
      navBarItemInViewModes: [
        viewModes.Location,
        viewModes.Region,
        viewModes.District,
        viewModes.Organization,
        viewModes.Master,
      ],
    },
    organizations: {
      path: '/organizations',
      pageName: 'Organizations',
      users: [actorTypes.MasterAdmin],
      viewModes: [viewModes.Master],
      navBarItemInViewModes: [viewModes.Master],
    },
    menuPricing: {
      path: '/menu-pricing',
      pageName: 'Menu pricing',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.Technician,
      ],
      viewModes: [],
      navBarItemInViewModes: [],
    },
    oilChangePricing: {
      path: '/oil-change-pricing',
      pageName: 'Oil change pricing',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.Technician,
      ],
      viewModes: [viewModes.Location],
      navBarItemInViewModes: [viewModes.Location],
    },
    oilType: {
      path: '/oil-type',
      pageName: 'Oil type',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.Technician,
      ],
      viewModes: [],
      navBarItemInViewModes: [],
    },
    scheduling: {
      path: '/scheduling',
      pageName: 'Scheduling',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
      ],
      viewModes: [viewModes.Location, viewModes.Region, viewModes.District, viewModes.Organization],
      navBarItemInViewModes: [viewModes.Location, viewModes.Region, viewModes.District, viewModes.Organization],
    },
    services: {
      path: '/services',
      pageName: 'Services',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
      ],
      viewModes: [viewModes.Location, viewModes.Region, viewModes.District, viewModes.Organization],
      navBarItemInViewModes: [viewModes.Location, viewModes.Region, viewModes.District, viewModes.Organization],
    },
    createServiceProfile: {
      path: 'services/create-service-profile',
      users: [actorTypes.MasterAdmin, actorTypes.OrganizationAdmin, actorTypes.OrganizationOwner],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District],
      navBarItemInViewModes: [viewModes.Organization, viewModes.Region, viewModes.District],
    },
    organizationMasterServices: {
      path: 'services/organization-master-services',
      users: [actorTypes.MasterAdmin, actorTypes.OrganizationAdmin, actorTypes.OrganizationOwner],
      viewModes: [viewModes.Organization],
      navBarItemInViewModes: [viewModes.Organization],
    },
    editServiceProfile: {
      path: 'services/edit-service-profile',
      users: [actorTypes.MasterAdmin, actorTypes.OrganizationAdmin, actorTypes.OrganizationOwner],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District],
      navBarItemInViewModes: [viewModes.Region, viewModes.District],
    },
    viewServiceProfile: {
      path: 'services/view-service-profile',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
      ],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District],
      navBarItemInViewModes: [viewModes.Region, viewModes.District],
    },
    newSettings: {
      path: '/settings/new',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
      ],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
      navBarItemInViewModes: [],
    },
    settings: {
      path: '/settings',
      pageName: 'Settings',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
      ],
      viewModes: [viewModes.Master, viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
      navBarItemInViewModes: [
        viewModes.Master,
        viewModes.Organization,
        viewModes.Region,
        viewModes.District,
        viewModes.Location,
      ],
    },
    newLocation: {
      path: '/settings/new-location',
      pageName: 'New Location',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
      ],
      viewModes: [viewModes.Master, viewModes.Organization, viewModes.Region, viewModes.District],
      navBarItemInViewModes: [],
    },
    marketing: {
      path: '/marketing',
      pageName: 'Marketing',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.Marketing,
      ],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
      navBarItemInViewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
    },
    aiChat: {
      path: '/ai-chat',
      pageName: 'AI Chat',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.OrganizationCustomerSupport,
        actorTypes.RegionalCustomerSupport,
      ],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
      navBarItemInViewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
    },
    dviAdvisor: {
      path: '/dvi-advisor',
      pageName: 'DVI Advisor',
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
        actorTypes.AssistantGeneralManager,
        actorTypes.ServiceAdvisor,
        actorTypes.OrganizationCustomerSupport,
        actorTypes.RegionalCustomerSupport,
      ],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
      navBarItemInViewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
    },
    yinonOnly: {
      path: '/yinon-only',
      pageName: 'Yinon only',
      users: [actorTypes.MasterAdmin, actorTypes.OrganizationAdmin, actorTypes.OrganizationOwner],
      viewModes: [viewModes.Organization],
      navBarItemInViewModes: [],
    },
    notFound: {
      path: '/not-found',
      users: [],
      viewModes: [],
      navBarItemInViewModes: [],
    },
    unifiedFlow: {
      path: '/unified-flow',
      pageName: 'UnifiedFlow',
      fullWidth: true,
      users: [
        actorTypes.MasterAdmin,
        actorTypes.OrganizationAdmin,
        actorTypes.OrganizationOwner,
        actorTypes.RegionalGeneralManager,
        actorTypes.DistrictManager,
        actorTypes.LocationManager,
      ],
      viewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
      navBarItemInViewModes: [viewModes.Organization, viewModes.Region, viewModes.District, viewModes.Location],
    },
  },
  whitelistedPaths: ['/calendar-pig', '/calendar-horse', '/calendar-rooster'],
  actorTypes,
  actorRanks,
  viewModes,
  ADMIN_APP_MAX_RANK,
  // Enable SDC (off by default)
  useSdc: false,
  // The Dogstatsd configuration
  dogstatsd: {
    host: '172.17.0.1',
    port: 8125,
    socket: undefined,
    options: {
      global_tags: [`app:${appName}`, `env:${process.env.CARDASH_ENV}`],
    },
  },
  // ga analytics enabled on prod
  analyticsEnabled: false,
  sfacOrganizationId: 1,
};
